import React, { createElement, useState } from 'react'

import Button from './Button'
import Checkbox from './Checkbox'
import {
  computeOptionsValidity,
  computeOptionValidity,
} from '../../../../../shared/js/options-utils'
import ImageInput from './ImageInput'
import Input from './Input'
import OptionSummary from './OptionSummary'
import Radios from './Radios'
import Select from './Select'
import TextArea from './TextArea'
import {
  ProductOptionWithNameProps,
  ProductOptionValueProps,
} from '../../../../../shared/js/Module'

export interface OptionComponentProps extends ProductOptionWithNameProps {
  onCancel?: Function
  optionsComponents?: { [key: string]: any }
  deleteDisabled?: boolean
  SummaryComponent?: any
  [key: string]: any
}

export interface TweakedOption {
  date: Date | string
  options: ProductOptionWithNameProps[]
  userName: string
}

// Option kinds that won‘t need manual validation on option value change/update.
export const autoSubmittingKinds = ['boolean', 'selection']

const optionComponentsMap = {
  button: Button,
  checkbox: Checkbox,
  image: ImageInput,
  input: Input,
  radio: Radios,
  select: Select,
  text: TextArea,
}

// Dynamically load component depending on expected display mode.
// Display/Component mapping is done with `optionsComponents`.
const Option = ({
  displayRepresentation,
  onCancel,
  onChange,
  onFontChange,
  optionsComponents = optionComponentsMap,
  deleteDisabled = false,
  SummaryComponent = OptionSummary,
  valid = true,
  ...props
}: OptionComponentProps) => {
  const optionComponent = optionsComponents[displayRepresentation]
  const [optionsValidity, setOptionsValidity] = useState<
    Record<string, boolean>
  >({})
  const [fontClassName, setFontClassName] = useState(undefined)
  const [focusableOptionId, setFocusableOptionId] = useState<number | null>(
    null
  )

  if (optionComponent === undefined) {
    return null
  }

  const { values } = props
  const options: ProductOptionWithNameProps[] = values[0]?.options || []

  const optionsAreValid = computeOptionsValidity(options)

  // Group siblings for display purpose
  const groupedOptions = groupSiblingOptions(options)

  const children = groupedOptions?.map((options, groupIndex) => {
    return (
      <fieldset key={groupIndex} className='right-action-modal__separator'>
        {options.map((option: ProductOptionWithNameProps, optIndex: number) => {
          const nextOption =
            options[optIndex + 1] || groupedOptions[groupIndex + 1]?.[0]
          const selectable = ['select', 'radio'].includes(
            option.displayRepresentation
          )

          return (
            <Option
              {...option}
              triggerFocus={focusableOptionId === option.id}
              key={option.id}
              fontClassName={fontClassName}
              name={`${option.displayRepresentation}-${option.id}`}
              onChange={(id: number, values: ProductOptionValueProps[]) => {
                handleChildChange(id, values)
                selectable && setFocusableOptionId(nextOption?.id)
              }}
              onFontChange={setFontClassName}
              optionsComponents={optionsComponents}
              valid={
                optionsValidity[option.id] === undefined ||
                optionsValidity[option.id]
              }
            />
          )
        })}
      </fieldset>
    )
  })

  return (
    <>
      <SummaryComponent deleteDisabled onCancel={onCancel} {...props} />
      {createElement(
        optionComponent,
        {
          ...props,
          name: `${displayRepresentation}-${props.id}`,
          onCancel,
          onChange,
          onFontChange,
          // When options exist, mix options validity with current option validity
          valid: options.length ? optionsAreValid && valid : valid,
        },
        children
      )}
    </>
  )

  // Manage children updates transfer to `onchange` callback.
  function handleChildChange(
    optionId: number,
    optValues: ProductOptionValueProps[]
  ) {
    const updatedOptions = options?.map((option: ProductOptionWithNameProps) =>
      option.id !== optionId ? option : { ...option, values: optValues }
    )
    const updatedValues = [{ ...values[0], options: updatedOptions }]
    onChange?.(props.id, updatedValues)

    // Check if current option is valid after change
    const updatedOption = updatedOptions.find(({ id }) => id === optionId)

    if (!updatedOption) return

    setOptionsValidity({
      ...optionsValidity,
      [optionId]: computeOptionValidity(updatedOption),
    })
  }
}

const REGEX_LABEL = /^(.+?)(\s+:)?\s*$/

export function adjustLabelForOptionality(label: string) {
  const result = label.match(REGEX_LABEL)
  const [, coreLabel, suffix] = result || []
  return (
    <>
      {coreLabel}
      {suffix}
    </>
  )
}

// When many options with the same display representation follow each other then
// group them (for display purpose).
function groupSiblingOptions(
  options: ProductOptionWithNameProps[]
): Array<ProductOptionWithNameProps[]> {
  const groupedOptions: Array<ProductOptionWithNameProps[]> = []
  let previousDisplayRep

  for (const option of options) {
    if (previousDisplayRep === option.displayRepresentation) {
      const lastGroup = groupedOptions.pop() || []
      groupedOptions.push([...lastGroup, option])
    } else {
      groupedOptions.push([option])
    }
    previousDisplayRep = option.displayRepresentation
  }

  return groupedOptions
}

export default Option
